import { useMemo } from "react";

import {
  AppBar,
  CircularProgressCenter,
  Drawer,
  FireCMS,
  FireCMSi18nProvider,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useBuildLocalConfigurationPersistence,
  useBuildModeController,
  useBuildNavigationController,
  useValidateAuthenticator
} from "@firecms/core";
import {
  FirebaseAuthController,
  FirebaseLoginView,
  FirebaseSignInProvider,
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";

import { adminAuthenticator } from "./auth/admin-authenticator";
import { coreCollections } from "./collections/core";
import { firebaseConfig } from "./config/firebase";

const brandLogo = "/frienzzi-logo.png";
const brandIcon = "/icon.png";

function AdminTitle() {
  return (
    <div className="frienzzi-admin-title">
      <img src={brandLogo} alt="Frienzzi" />
      <span>Admin</span>
    </div>
  );
}

function App() {
  const collections = useMemo(() => coreCollections, []);

  const {
    firebaseApp,
    firebaseConfigLoading,
    configError
  } = useInitialiseFirebase({
    firebaseConfig
  });

  const modeController = useBuildModeController();
  const signInOptions: FirebaseSignInProvider[] = ["password"];

  const authController: FirebaseAuthController = useFirebaseAuthController({
    firebaseApp,
    signInOptions
  });

  const userConfigPersistence = useBuildLocalConfigurationPersistence();

  const firestoreDelegate = useFirestoreDelegate({
    firebaseApp
  });

  const storageSource = useFirebaseStorageSource({
    firebaseApp
  });

  const {
    authLoading,
    canAccessMainView,
    notAllowedError
  } = useValidateAuthenticator({
    authController,
    authenticator: adminAuthenticator,
    dataSourceDelegate: firestoreDelegate,
    storageSource
  });

  const navigationController = useBuildNavigationController({
    disabled: authLoading,
    collections,
    authController,
    dataSourceDelegate: firestoreDelegate
  });

  if (firebaseConfigLoading || !firebaseApp) {
    return <CircularProgressCenter />;
  }

  if (configError) {
    return <CenteredView>{configError}</CenteredView>;
  }

  return (
    <FireCMSi18nProvider>
      <SnackbarProvider>
        <ModeControllerProvider value={modeController}>
          <FireCMS
            navigationController={navigationController}
            authController={authController}
            userConfigPersistence={userConfigPersistence}
            dataSourceDelegate={firestoreDelegate}
            storageSource={storageSource}
          >
            {({ loading }) => {
              if (loading || authLoading) {
                return <CircularProgressCenter size="large" />;
              }

              if (!canAccessMainView) {
                return (
                  <FirebaseLoginView
                    className="frienzzi-login-view"
                    authController={authController}
                    firebaseApp={firebaseApp}
                    logo={brandLogo}
                    signInOptions={signInOptions}
                    disableSignupScreen
                    notAllowedError={notAllowedError}
                    additionalComponent={
                      <p className="frienzzi-login-note">
                        Authorized Frienzzi administrators only.
                      </p>
                    }
                  >
                    <div className="frienzzi-login-heading">
                      <span>Admin Console</span>
                    </div>
                  </FirebaseLoginView>
                );
              }

              return (
                <Scaffold
                  autoOpenDrawer={false}
                  className="frienzzi-admin-shell"
                  logo={brandIcon}
                >
                  <AppBar title={<AdminTitle />} logo={brandIcon} />
                  <Drawer />
                  <NavigationRoutes />
                  <SideDialogs />
                </Scaffold>
              );
            }}
          </FireCMS>
        </ModeControllerProvider>
      </SnackbarProvider>
    </FireCMSi18nProvider>
  );
}

export default App;
